import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { authService } from '../services/apiService.jsx';
import './Login.css';

const VerifyOTP = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  const [email, setEmail] = useState(location.state?.email || '');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [timeLeft, setTimeLeft] = useState(600); // 10 minutes

  // Timer for OTP expiration
  useEffect(() => {
    if (timeLeft <= 0) {
      setError('OTP has expired. Please request a new one.');
      return;
    }

    const timer = setInterval(() => {
      setTimeLeft((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();

    if (!email || !otp) {
      setError('Please enter both email and OTP');
      return;
    }

    setError('');
    setMessage('');
    setLoading(true);

    try {
      await authService.validateOTP(email, otp);
      setMessage('✅ OTP verified! Proceeding to reset password...');
      
      // Redirect to reset password page
      setTimeout(() => {
        navigate('/reset-password', { state: { email, otp } });
      }, 2000);
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Invalid or expired OTP');
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setError('');
    setMessage('');
    setLoading(true);

    try {
      await authService.sendOTP(email);
      setMessage('✅ New OTP sent to your email!');
      setOtp('');
      setTimeLeft(600); // Reset timer
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Failed to resend OTP');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-split-box">
        <div className="login-card">
          <div className="login-card-content">
            <h2>Verify OTP</h2>
            <p style={{ color: '#666', fontSize: '14px', marginBottom: '20px' }}>
              Enter the 6-digit OTP sent to your email address.
            </p>

            {message && <div className="alert alert-success">{message}</div>}
            {error && <div className="alert alert-error">{error}</div>}

            <form onSubmit={handleVerifyOTP}>
              <div className="input-field">
                <label className="input-label">Email</label>
                <div className="input-wrapper">
                  <input
                    type="email"
                    className="form-input"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled
                    placeholder="Email"
                  />
                </div>
              </div>

              <div className="input-field">
                <label className="input-label">OTP</label>
                <div className="input-wrapper">
                  <input
                    type="text"
                    className="form-input"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="Enter 6-digit OTP"
                    maxLength="6"
                    required
                    disabled={loading}
                  />
                </div>
                <div style={{ marginTop: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <small style={{ color: '#666' }}>
                    💡 Check your email for the OTP code
                  </small>
                  <small style={{ color: timeLeft < 120 ? '#ff6b6b' : '#999' }}>
                    ⏱️ {formatTime(timeLeft)}
                  </small>
                </div>
              </div>

              <button className="login-button" type="submit" disabled={loading || timeLeft <= 0}>
                {loading ? 'Verifying...' : 'Verify OTP'}
              </button>
            </form>

            <div style={{ marginTop: '20px', textAlign: 'center' }}>
              <p style={{ fontSize: '12px', color: '#999' }}>
                Didn't receive the OTP?{' '}
                <button
                  type="button"
                  onClick={handleResendOTP}
                  disabled={loading}
                  style={{
                    background: 'transparent',
                    color: '#3b82f6',
                    border: 'none',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    fontSize: '12px'
                  }}
                >
                  Resend OTP
                </button>
              </p>
              <p style={{ fontSize: '12px', color: '#999', marginTop: '10px' }}>
                <a href="/forgot-password" style={{ color: '#3b82f6', textDecoration: 'none' }}>Start Over</a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyOTP;
